<template>
  <div class="min-h-screen">
    <SiteHeader :page-name="'Ledger Dashboard'" :full-screen-editing="editing" :editing-title />
    <template v-if="ledger">
      <Card pt:root:class="rounded-t-none! rounded-b-none!">
        <template #content>
          <div class="flex flex-row justify-between">
            <div class="flex flex-col w-fit justify-start gap-3">
              <div class="flex flex-row items-center">
                <h1 class="text-4xl font-bold">{{ ledger.checkingAccount.description }}</h1>
                <Tag v-if="ledger.checkingAccount.active" rounded class="mx-4 h-8 my-auto" severity="success">Active</Tag>
                <Tag v-else rounded class="mx-4 h-8 my-auto" severity="danger">Inactive</Tag>
                <IconButton icon="pencil" outlined small @click="editLedger = true"></IconButton>
              </div>
            </div>
            <div>
              <ReportsListButton :context="'SINGLE_LEDGER'" :checkingAccountId="ledger.checkingAccount.checkingAccountId"></ReportsListButton>
            </div>
            <div class="flex flex-row w-fit my-auto gap-2">
              <Chip icon="pi pi-users" class="h-10 font-bold" :label="ledger.checkingAccount.userGroup.name"></Chip>
            </div>
          </div>
        </template>
      </Card>
      <Tabs
        v-if="!editing"
        v-model:value="activeTab"
        class="text-lg border-surface-100 border-1 border-t-0 dark:border-surface-900 rounded-b-lg shadow"
      >
        <TabList>
          <Tab v-for="(tab, index) in tabs" :value="index" v-show="tab.visible">
            <i :class="tab.icon" class="mr-2"></i>
            <span>{{ tab.label }}</span>
          </Tab>
        </TabList>
      </Tabs>
      <LedgerInfoTab v-if="activeTab === 0" ref="ledgerInfoTab" v-model:ledger="ledger"></LedgerInfoTab>
      <PaymentTable v-if="activeTab === 1" v-model:ledger="ledger.checkingAccount" :permissions="userPermissions" showTotals></PaymentTable>
      <LedgerExportConfig v-if="activeTab === 2" v-model:ledger="ledger" v-model:editing="editing" v-model:editing-title="editingTitle"
        >Export Configuration</LedgerExportConfig
      >
      <TSDialog v-model:visible="editLedger" label="Edit Ledger" icon="pencil" v-on:after-hide="editLedger = false" size="w-[500px]">
        <template #content>
          <EditLedger v-model:ledger="ledger.checkingAccount" @edited="ledgerEdited" @cancelled="editLedger = false" />
        </template>
      </TSDialog>
    </template>
    <template v-else>
      <div class="flex flex-col w-full mt-20">
        <ProgressSpinner style="width: 200px; height: 200px" class="fill-surface-0 dark:fill-surface-800 mx-auto" animationDuration=".5s" />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { getUserPermissions } from "@/services/Permissions";
import { useAuthStore } from "@/stores/auth";
import { LedgerDashboardSchema, type LedgerDashboard } from "@/types/Ledger";
import { UserPermissionsSchema, type UserPermissions } from "@/types/User";

const route = useRoute();
const ledger = ref<LedgerDashboard>();

const activeTab = ref<number>(0);
const editLedger = ref<boolean>(false);

const userStore = useAuthStore();
const userPermissions = ref<UserPermissions>(UserPermissionsSchema.parse({}));

const editing = ref<boolean>(false);
const editingTitle = ref<string>("");

const ledgerInfoTab = ref();
const initialLoadComplete = ref(false);

const tabs = ref([
  { label: "Summary", icon: "pi pi-info-circle", visible: true },
  { label: "Payments", icon: "pi pi-dollar", visible: true },
  { label: "Export Configuration", icon: "pi pi-wrench", visible: true },
]);

watch(
  () => activeTab.value,
  (tab) => {
    nextTick(() => {
      if (tab === 0 && initialLoadComplete.value && ledgerInfoTab.value) ledgerInfoTab.value.dataLoaded();
    });
  }
);

onMounted(async (): Promise<void> => {
  await getLedgerDetails();
  await getUserPermissions(userStore.getUserInfo().userId).then((result: UserPermissions) => (userPermissions.value = result));
  initialLoadComplete.value = true;
  ledgerInfoTab.value.dataLoaded();
});

const getLedgerDetails = async (): Promise<void> => {
  await axios
    .get(`/ledger/${route.params.id}`)
    .then((response) =>
      handleApiResponse(
        response,
        (data: any) => (ledger.value = LedgerDashboardSchema.parse(data)),
        (errors: ApiError[]) => error("An error occurred", errors[0].message)
      )
    )
    .catch((e) => errorApi(e));
};

const ledgerEdited = (): void => {
  editLedger.value = false;
  getLedgerDetails();
};
</script>
